import { resolveZombieCrushersApiUrl } from "./apiConfig";

const defaultHeaders = {
  Accept: "application/json"
};

const ARRAY_RESPONSE_KEYS = [
  "results",
  "kills",
  "data",
  "items",
  "streamers",
  "entries",
  "leaderboard",
  "list"
];

const AUTH_STORAGE_KEYS = [
  "zombieCrushersAccessToken",
  "zcAccessToken",
  "zc.authToken",
  "ZC_ACCESS_TOKEN"
];

const ENV_AUTH_TOKEN = [
  "REACT_APP_ZOMBIECRUSHERS_TOKEN",
  "REACT_APP_ZOMBIECRUSHERS_API_TOKEN",
  "REACT_APP_ZC_ACCESS_TOKEN",
  "REACT_APP_ZC_API_TOKEN"
]
  .map(key => {
    const value = process.env[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
    return null;
  })
  .find(Boolean) || null;

let explicitAuthToken = null;
let streamersDirectoryCache = null;
let streamersDirectoryPromise = null;

function extractList(payload, seen = new Set()) {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }
  if (seen.has(payload)) {
    return [];
  }
  seen.add(payload);

  for (const key of ARRAY_RESPONSE_KEYS) {
    const value = payload[key];
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === "object") {
      const nested = extractList(value, seen);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
}

function normalizeUnlockedValue(value) {
  if (value === true || value === false) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    if (
      [
        "true",
        "1",
        "yes",
        "y",
        "s",
        "si",
        "unlocked",
        "completed",
        "complete",
        "earned",
        "achieved",
        "obtained",
        "sbloccato"
      ].includes(normalized)
    ) {
      return true;
    }
    if (
      [
        "false",
        "0",
        "no",
        "n",
        "locked",
        "available",
        "pending",
        "missing",
        "bloccato",
        "da sbloccare"
      ].includes(normalized)
    ) {
      return false;
    }
  }
  if (typeof value === "number") {
    if (!Number.isNaN(value)) {
      if (value > 0) {
        return true;
      }
      if (value === 0) {
        return false;
      }
    }
  }
  return null;
}

function getUnlockedState(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }
  const direct = normalizeUnlockedValue(entry.unlocked);
  if (direct !== null) {
    return direct;
  }

  const altKeys = [
    "is_unlocked",
    "isUnlocked",
    "completed",
    "earned",
    "status",
    "state"
  ];

  for (const key of altKeys) {
    if (key in entry) {
      const normalized = normalizeUnlockedValue(entry[key]);
      if (normalized !== null) {
        return normalized;
      }
    }
  }

  const timestampKeys = [
    "unlocked_at",
    "unlockedAt",
    "earned_at",
    "earnedAt",
    "completed_at",
    "completedAt"
  ];

  for (const key of timestampKeys) {
    if (entry[key]) {
      return true;
    }
  }

  return null;
}

function withUnlockedFlag(entry, { forceUnlocked } = {}) {
  if (!entry || typeof entry !== "object") {
    return entry;
  }

  let unlockedValue = getUnlockedState(entry);

  if (unlockedValue === null && typeof forceUnlocked === "boolean") {
    unlockedValue = forceUnlocked;
  }

  if (unlockedValue === null) {
    if (typeof entry.unlocked === "boolean") {
      return entry;
    }
    return { ...entry, unlocked: false };
  }

  const normalized = Boolean(unlockedValue);
  if (entry.unlocked === normalized && typeof entry.unlocked === "boolean") {
    return entry;
  }

  return { ...entry, unlocked: normalized };
}

function countUnlocked(list) {
  if (!Array.isArray(list) || list.length === 0) {
    return 0;
  }
  return list.reduce((acc, item) => (getUnlockedState(item) === true ? acc + 1 : acc), 0);
}

function buildUrl(path, params) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const url = resolveZombieCrushersApiUrl(cleanPath);
  if (!params || Object.keys(params).length === 0) {
    return url;
  }
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    searchParams.append(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
}

function readTokenFromStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  const storages = [window.localStorage, window.sessionStorage].filter(Boolean);

  for (const storage of storages) {
    for (const key of AUTH_STORAGE_KEYS) {
      try {
        const value = storage.getItem(key);
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }
      } catch (error) {
        /* ignore storage access issues */
      }
    }
  }

  try {
    const globalToken = window.ZC_ACCESS_TOKEN;
    if (typeof globalToken === "string" && globalToken.trim().length > 0) {
      return globalToken.trim();
    }
  } catch (error) {
    /* ignore window access issues */
  }

  return null;
}

export function setZombieCrushersAuthToken(token) {
  if (typeof token === "string") {
    const trimmed = token.trim();
    explicitAuthToken = trimmed.length > 0 ? trimmed : null;
  } else {
    explicitAuthToken = token ?? null;
  }
}

export function clearZombieCrushersAuthToken() {
  explicitAuthToken = null;
}

export function getZombieCrushersAuthToken() {
  if (explicitAuthToken) {
    return explicitAuthToken;
  }

  const storageToken = readTokenFromStorage();
  if (storageToken) {
    return storageToken;
  }

  return ENV_AUTH_TOKEN;
}

function buildHeaders(extraHeaders = {}, { requireAuth = false } = {}) {
  const headers = { ...defaultHeaders, ...extraHeaders };

  if (requireAuth) {
    if (!headers.Authorization) {
      const token = getZombieCrushersAuthToken();
      if (!token) {
        throw new Error(
          "Per accedere agli achievement è necessario configurare un token di autenticazione."
        );
      }
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
}

async function requestJson(path, { method = "GET", params, body, headers, requireAuth = false } = {}) {
  const normalizedMethod = String(method || "GET").toUpperCase();
  let requestUrl = buildUrl(path, normalizedMethod === "GET" || normalizedMethod === "HEAD" ? params : undefined);

  if (normalizedMethod !== "GET" && normalizedMethod !== "HEAD" && params && Object.keys(params).length > 0) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        return;
      }
      query.append(key, String(value));
    });
    const suffix = query.toString();
    if (suffix) {
      requestUrl = `${requestUrl}${requestUrl.includes("?") ? "&" : "?"}${suffix}`;
    }
  }

  const fetchOptions = {
    method: normalizedMethod,
    headers: buildHeaders(headers, { requireAuth }),
    credentials: "include"
  };

  if (body !== undefined && body !== null) {
    fetchOptions.body = typeof body === "string" ? body : JSON.stringify(body);
    if (!fetchOptions.headers["Content-Type"]) {
      fetchOptions.headers["Content-Type"] = "application/json";
    }
  }

  const response = await fetch(requestUrl, fetchOptions);

  if (!response.ok) {
    const message = await safeParseError(response);
    throw new Error(message);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

async function safeParseError(response) {
  try {
    const data = await response.json();
    if (typeof data === "string") {
      return data;
    }
    if (data && typeof data.message === "string") {
      return data.message;
    }
  } catch (error) {
    /* swallow JSON parse errors */
  }
  return `Request failed with status ${response.status}`;
}

function ensureTwitchId(twitchUserId) {
  if (twitchUserId === undefined || twitchUserId === null) {
    throw new Error("Streamer id non valido");
  }
  const trimmed = String(twitchUserId).trim();
  if (!trimmed) {
    throw new Error("Streamer id non valido");
  }
  return trimmed;
}

export async function fetchLeaderboard(limit = 10) {
  const normalizedLimit =
    typeof limit === "string" && ["all", "full"].includes(limit.toLowerCase())
      ? limit
      : Number.isFinite(limit)
      ? Math.max(1, limit)
      : 10;

  const response = await requestJson("/zombiecrushers/streamers/leaderboard", {
    params: { limit: normalizedLimit }
  });

  return extractList(response);
}

export function fetchRankings() {
  return requestJson("/zombiecrushers/streamers/rankings");
}

export async function searchStreamers(query, limit = 20) {
  const trimmedQuery = query?.trim();
  if (!trimmedQuery) {
    return [];
  }

  const cappedLimit = Math.min(Math.max(Number(limit) || 20, 1), 50);

  const response = await requestJson("/zombiecrushers/streamers/search", {
    params: { q: trimmedQuery, limit: cappedLimit }
  });

  return extractList(response);
}

export async function fetchStreamersDirectory({ forceRefresh = false } = {}) {
  if (!forceRefresh) {
    if (Array.isArray(streamersDirectoryCache)) {
      return streamersDirectoryCache;
    }

    if (streamersDirectoryPromise) {
      return streamersDirectoryPromise;
    }
  } else {
    streamersDirectoryCache = null;
  }

  const promise = requestJson("/zombiecrushers/streamers/rankings")
    .then(response => {
      const list = extractList(response);
      streamersDirectoryCache = list;
      return list;
    })
    .catch(error => {
      streamersDirectoryCache = null;
      throw error;
    })
    .finally(() => {
      streamersDirectoryPromise = null;
    });

  streamersDirectoryPromise = promise;
  return promise;
}

export async function fetchAchievementDefinitions(twitchUserId) {
  const id = ensureTwitchId(twitchUserId); // ora obbligatorio
  const response = await requestJson(
    "/zombiecrushers/achievements/definitions",
    { params: { twitchUserId: id } }
  );

  const rawAchievements = Array.isArray(response)
    ? response
    : Array.isArray(response?.achievements)
    ? response.achievements
    : [];

  const achievements = rawAchievements.map(item => withUnlockedFlag(item));

  let totalAvailable = null;
  if (response && (response.totalAvailable ?? response.total_available) !== undefined) {
    totalAvailable = response.totalAvailable ?? response.total_available;
  } else if (achievements.length > 0) {
    totalAvailable = achievements.length;
  }

  const totalSessions =
    response && (response.totalSessions ?? response.total_sessions) !== undefined
      ? response.totalSessions ?? response.total_sessions
      : null;

  let totalUnlocked = null;
  if (response && (response.totalUnlocked ?? response.total_unlocked) !== undefined) {
    totalUnlocked = response.totalUnlocked ?? response.total_unlocked;
  } else if (achievements.length > 0) {
    totalUnlocked = countUnlocked(achievements);
  }

  return {
    achievements,
    totalUnlocked,
    totalAvailable,
    totalSessions,
    raw: response,
    twitchUserId: id
  };
}

export async function fetchUnlockedAchievements(twitchUserId) {
  const id = ensureTwitchId(twitchUserId);
  const response = await requestJson("/zombiecrushers/achievements/unlocked", {
    params: { twitchUserId: id }
  });

  const rawAchievements = Array.isArray(response)
    ? response
    : Array.isArray(response?.achievements)
    ? response.achievements
    : [];

  const achievements = rawAchievements.map(item => withUnlockedFlag(item, { forceUnlocked: true }));

  const totalUnlocked =
    response && (response.totalUnlocked ?? response.total_unlocked) !== undefined
      ? response.totalUnlocked ?? response.total_unlocked
      : achievements.length > 0
      ? achievements.length
      : null;

  const totalAvailable =
    response && (response.totalAvailable ?? response.total_available) !== undefined
      ? response.totalAvailable ?? response.total_available
      : null;

  return {
    totalUnlocked,
    totalAvailable,
    achievements,
    raw: response
  };
}

export async function reportAchievementSession(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error("Payload non valido per il report della sessione");
  }

  const id = ensureTwitchId(payload.twitchUserId);
  const body = { ...payload, twitchUserId: id };

  return requestJson("/zombiecrushers/achievements/report", {
    method: "POST",
    body,
    requireAuth: true
  });
}

export async function unlockAchievement(payload) {
  if (!payload || typeof payload !== "object") {
    throw new Error("Payload non valido per lo sblocco manuale");
  }

  const achievement = payload.achievement || payload.achievement_code || payload.code;
  const streamerId = payload.streamerId || payload.streamer_id || payload.twitchUserId;

  if (!achievement || String(achievement).trim().length === 0) {
    throw new Error("Achievement code non valido");
  }

  const id = ensureTwitchId(streamerId);

  return requestJson("/zombiecrushers/achievements/unlock", {
    method: "POST",
    body: {
      achievement: String(achievement).trim(),
      streamerId: id,
      sessionId: payload.sessionId ?? payload.session_id ?? undefined
    },
    requireAuth: true
  });
}

export async function fetchStreamerProfile(twitchUserId) {
  const id = ensureTwitchId(twitchUserId);
  const profile = await requestJson(
    `/zombiecrushers/streamers/${encodeURIComponent(id)}/profile`
  );

  // Il backend ora ritorna già achievements.definitions con {unlocked, unlocked_at}
  const definitions = Array.isArray(profile?.achievements?.definitions)
    ? profile.achievements.definitions.map(item => withUnlockedFlag(item))
    : [];

  const totalUnlocked =
    profile?.achievements?.totalUnlocked ?? countUnlocked(definitions);
  const totalAvailable =
    profile?.achievements?.totalAvailable ?? (definitions.length || null);

  return {
    ...profile,
    achievements: {
      ...profile.achievements,
      definitions,
      totalUnlocked,
      totalAvailable
    },
    achievementsSummary: {
      totalUnlocked,
      totalAvailable,
      totalSessions: profile?.stats?.total_sessions ?? null
    },
    achievementsError: null
  };
}








